'use client';

import { useQuery } from '@tanstack/react-query';
import { BorrowMarketData } from '../useSyncBorrowLendBalances';
import { AprDetails, MarketInterestData } from './types';
import { BigNum } from '@drift-labs/sdk';
import { getApyFromApr } from 'src/utils/borrowLend';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { useSpotMarkets } from '../spotMarkets/useSpotMarkets';
import { useSpotMarketsLookup } from '../spotMarkets/useSpotMarketsLookup';
import useFeatureFlagStore from 'src/stores/featureFlag/useFeatureFlagStore';
import useGetOraclePriceForMarket from '../useGetOraclePriceForMarket';
import { MarketId } from '@drift/common';
import Env, {
	BNSOL_MARKET_INDEX,
	EXPONENT_POOL_JITOSOL_MARKET_INDEX,
	FRAG_SOL_MARKET_INDEX,
	KY_SOL_MARKET_INDEX,
	JLP_POOL_JLP_MARKET_INDEX,
	SUSDE_MARKET_INDEX,
	JLP_MARKET_INDEX,
	LST_POOL_JITOSOL_MARKET_INDEX,
	SYRUP_USDC_MARKET_INDEX,
	USDS_MARKET_INDEX,
	EURC_MARKET_INDEX,
	EXPONENT_POOL_JTO_MARKET_INDEX,
	LBTC_MARKET_INDEX,
	USDE_MARKET_INDEX,
	CASH_MARKET_INDEX,
} from 'src/environmentVariables/EnvironmentVariables';
import {
	processSanctumLstData,
	processBnsolData,
	processExponentData,
	processBsolData,
	processSusdeData,
	processJlpData,
	processSyrupUsdcData,
	processLbtcData,
} from './utils';
import {
	getUsdeRewardMetric,
	calculateUsdsRewards,
	calculateEurcRewards,
	calculateExponentPoolJtoRewards,
	calculateSyrupUsdcRewards,
	calculateLbtcUsdcRewards,
	calculateCashRewards,
	calculateCashBorrowRewards,
} from './rewardCalculators';
import { useBNsolFetch } from '../interestBreakdown/useBNsolFetch';
import { useBSolFetch } from '../interestBreakdown/useBSolFetch';
import { useExponentLstFetch } from '../interestBreakdown/useExponentLstFetch';
import { useSanctumLstApiFetch } from '../interestBreakdown/useSanctumLstApiFetch';
import { useSusdeFetch } from '../interestBreakdown/useSusdeFetch';
import { useSyrupUsdcFetch } from '../interestBreakdown/useSyrupUsdcFetch';
import { useLbtcFetch } from '../interestBreakdown/useLbtcFetch';
import { useJlpJupPerpsPoolAccountData } from '../jlp/useJlpJupPerpsPoolAccountData';

const JITO_SOL_MARKET_INDEX = 6;

function calculateMarketInterestData(
	borrowLendData: BorrowMarketData,
	aprBreakdowns: AprDetails[]
) {
	const marketAprBreakdowns = aprBreakdowns ?? [];

	// Calculate deposit data
	const baseDepositApr = borrowLendData.depositApr
		.mul(BigNum.from(100))
		.toNum();
	const baseApy = getApyFromApr(baseDepositApr, 'hourly');
	const precision = borrowLendData.depositApr.precision;

	let nativeApy = 0;
	const promoApy = marketAprBreakdowns.reduce((acc, curr) => {
		if (curr.type === 'native') {
			nativeApy = curr.apy;
			return acc;
		}
		if (curr.type === 'borrow-reward') {
			return acc;
		}
		return acc + (curr.apy ?? 0);
	}, 0);

	const totalApyCandidate = baseApy + promoApy + nativeApy;
	const totalApy = Number.isFinite(totalApyCandidate) ? totalApyCandidate : 0;

	const baseApyBN = BigNum.fromPrint(
		Number.isFinite(baseApy) ? `${baseApy}` : '0',
		precision
	);
	const nativeApyBN = BigNum.fromPrint(
		Number.isFinite(nativeApy) ? `${nativeApy}` : '0',
		precision
	);
	const promoApyBN = BigNum.fromPrint(
		Number.isFinite(promoApy) ? `${promoApy}` : '0',
		precision
	);
	const totalApyBN = baseApyBN.add(nativeApyBN).add(promoApyBN);

	const depositBreakdowns = marketAprBreakdowns.filter(
		(apr) => apr.type !== 'borrow-reward'
	);

	// Calculate borrow data
	const baseBorrowApr = borrowLendData.borrowApr.mul(BigNum.from(100)).toNum();
	const baseBorrowApy = getApyFromApr(baseBorrowApr, 'hourly');

	const borrowRewardApy = marketAprBreakdowns.reduce((acc, curr) => {
		if (curr.type === 'borrow-reward') {
			return acc + (curr.apy ?? 0);
		}
		return acc;
	}, 0);

	const totalBorrowApy = baseBorrowApy - borrowRewardApy;
	const borrowBreakdowns = marketAprBreakdowns.filter(
		(apr) => apr.type === 'borrow-reward'
	);

	const nativeBreakdown = marketAprBreakdowns.find(
		(apr) => apr.type === 'native'
	);

	const borrowPrecision = borrowLendData.borrowApr.precision;
	return {
		deposit: {
			baseApy,
			totalApy,
			nativeApy,
			promoApy,
			breakdown: depositBreakdowns,
			totalApyBN,
			baseApyBN,
		},
		borrow: {
			baseApy: baseBorrowApy,
			totalApy: totalBorrowApy,
			rewardApy: borrowRewardApy,
			breakdown: borrowBreakdowns,
			totalApyBN: BigNum.fromPrint(
				Number.isFinite(totalBorrowApy) ? `${totalBorrowApy}` : '0',
				borrowPrecision
			),
			baseApyBN: BigNum.fromPrint(
				Number.isFinite(baseBorrowApy) ? `${baseBorrowApy}` : '0',
				borrowPrecision
			),
		},
		native: nativeBreakdown
			? {
					apy: nativeBreakdown.apy,
					source: nativeBreakdown.source || 'Native APY',
					breakdown: nativeBreakdown,
				}
			: undefined,
	};
}

/**
 * Comprehensive hook that provides all market data including interest calculations
 * Uses individual hooks for external data fetching with optimized caching
 */
export function useAllMarketInterestData(): {
	data: Record<number, MarketInterestData> | undefined;
	isLoading: boolean;
} {
	const borrowLendDataList = useDriftStore((s) => s.borrowLendData);
	const connection = useDriftStore((s) => s.connection.current);
	const spotMarkets = useSpotMarkets();
	const spotMarketsLookup = useSpotMarketsLookup();
	const exponentPoolRewardsEnabled = useFeatureFlagStore((s) =>
		s.flagEnabled('ENABLE_EXPONENT_POOL_REWARDS')
	);
	const getOraclePrice = useGetOraclePriceForMarket();

	// Individual external data hooks
	const sanctumLstQuery = useSanctumLstApiFetch();
	const bnsolQuery = useBNsolFetch();
	const exponentQuery = useExponentLstFetch();
	const bsolQuery = useBSolFetch();
	const susdeQuery = useSusdeFetch();
	const syrupUsdcQuery = useSyrupUsdcFetch();
	const lbtcQuery = useLbtcFetch();
	const jlpPoolData = useJlpJupPerpsPoolAccountData();

	// Environment variables for reward calculations
	const envConfig = {
		usdsBonusPerWeek: Env.usdsBonusPerWeek ?? 0,
		eurcBonusPerWeek: Env.eurcBonusPerWeek ?? 0,
		exponentPoolJtoBonusPerWeek: Env.exponentPoolJtoBonusPerWeek ?? 0,
		syrupUsdcBonusPerWeek: Env.syrupUsdcBonusPerWeek ?? 0,
		lbtcBonusPerWeek: Env.lbtcBonusPerWeek ?? 0,
		cashBonusPerWeek: Env.cashBonusPerWeek ?? 0,
		cashBorrowBonusPerWeek: Env.cashBorrowBonusPerWeek ?? 0,
	};

	const { data, isLoading } = useQuery({
		queryKey: ['unified-market-data'],
		queryFn: async () => {
			// Get external data from hooks
			const sanctumLstData = sanctumLstQuery.data;
			const bnsolData = bnsolQuery.data;
			const exponentData = exponentQuery.data;
			const bsolData = bsolQuery.data;
			const susdeData = susdeQuery.data;
			const syrupUsdcData = syrupUsdcQuery.data;
			const lbtcData = lbtcQuery.data;
			const jlpData = jlpPoolData;

			// Process native data using organized functions
			const nativeData: Record<number, AprDetails[]> = {};

			// Process all native data sources
			if (sanctumLstData && spotMarkets.length > 0) {
				Object.assign(
					nativeData,
					processSanctumLstData(
						sanctumLstData,
						spotMarkets,
						JITO_SOL_MARKET_INDEX,
						EXPONENT_POOL_JITOSOL_MARKET_INDEX,
						LST_POOL_JITOSOL_MARKET_INDEX
					)
				);
			}

			if (bnsolData) {
				Object.assign(
					nativeData,
					processBnsolData(bnsolData, BNSOL_MARKET_INDEX)
				);
			}

			if (exponentData) {
				Object.assign(
					nativeData,
					processExponentData(
						exponentData,
						KY_SOL_MARKET_INDEX,
						FRAG_SOL_MARKET_INDEX
					)
				);
			}

			if (bsolData) {
				// Adapt SDK response to our local type
				const adaptedBsolData = {
					stats: {
						apy: {
							base: bsolData.stats?.apy?.base ?? 0,
							lending: bsolData.stats?.apy?.lending ?? 1,
							blze: bsolData.stats?.apy?.blze ?? 0,
						},
					},
				};
				Object.assign(nativeData, processBsolData(adaptedBsolData));
			}

			if (susdeData) {
				Object.assign(
					nativeData,
					processSusdeData(susdeData, SUSDE_MARKET_INDEX)
				);
			}

			if (jlpData) {
				Object.assign(
					nativeData,
					processJlpData(jlpData, JLP_POOL_JLP_MARKET_INDEX, JLP_MARKET_INDEX)
				);
			}

			if (syrupUsdcData) {
				Object.assign(
					nativeData,
					processSyrupUsdcData(syrupUsdcData, SYRUP_USDC_MARKET_INDEX)
				);
			}

			if (lbtcData) {
				Object.assign(nativeData, processLbtcData(lbtcData, LBTC_MARKET_INDEX));
			}

			// Process reward data using organized functions
			const rewardData: Record<number, AprDetails[]> = {};

			// Static USDe metric
			rewardData[USDE_MARKET_INDEX] = getUsdeRewardMetric();

			// Get market data for reward calculations
			const marketsByIndex = borrowLendDataList.reduce(
				(acc, market) => {
					acc[market.bankConfig.marketIndex] = market;
					return acc;
				},
				{} as Record<number, BorrowMarketData>
			);

			// Calculate various rewards using organized functions
			if (envConfig.usdsBonusPerWeek) {
				const usdsRewards = calculateUsdsRewards(
					marketsByIndex[USDS_MARKET_INDEX],
					envConfig.usdsBonusPerWeek
				);
				if (usdsRewards.length > 0) {
					rewardData[USDS_MARKET_INDEX] = usdsRewards;
				}
			}

			if (envConfig.eurcBonusPerWeek) {
				const eurcRewards = calculateEurcRewards(
					marketsByIndex[EURC_MARKET_INDEX],
					envConfig.eurcBonusPerWeek
				);
				if (eurcRewards.length > 0) {
					rewardData[EURC_MARKET_INDEX] = eurcRewards;
				}
			}

			// JTO rewards for Exponent Pool
			if (envConfig.exponentPoolJtoBonusPerWeek && exponentPoolRewardsEnabled) {
				const jtoSpotMarket = spotMarketsLookup[EXPONENT_POOL_JTO_MARKET_INDEX];
				if (jtoSpotMarket) {
					const oraclePrice =
						getOraclePrice(
							MarketId.createSpotMarket(jtoSpotMarket.marketIndex)
						)?.toNum() ?? 0;

					const exponentRewards = calculateExponentPoolJtoRewards(
						marketsByIndex[EXPONENT_POOL_JITOSOL_MARKET_INDEX],
						envConfig.exponentPoolJtoBonusPerWeek,
						jtoSpotMarket,
						oraclePrice
					);
					if (exponentRewards.length > 0) {
						rewardData[EXPONENT_POOL_JITOSOL_MARKET_INDEX] = exponentRewards;
					}
				}
			}

			// USDC rewards for syrupUSDC
			if (envConfig.syrupUsdcBonusPerWeek) {
				const usdcSpotMarket = spotMarketsLookup[0]; // USDC market index 0
				if (usdcSpotMarket) {
					const syrupRewards = calculateSyrupUsdcRewards(
						marketsByIndex[SYRUP_USDC_MARKET_INDEX],
						envConfig.syrupUsdcBonusPerWeek,
						usdcSpotMarket
					);
					if (syrupRewards.length > 0) {
						rewardData[SYRUP_USDC_MARKET_INDEX] = syrupRewards;
					}
				}
			}

			// USDC rewards for LBTC
			if (envConfig.lbtcBonusPerWeek) {
				const usdcSpotMarket = spotMarketsLookup[0]; // USDC market index 0
				if (usdcSpotMarket) {
					const lbtcRewards = calculateLbtcUsdcRewards(
						marketsByIndex[LBTC_MARKET_INDEX],
						envConfig.lbtcBonusPerWeek,
						usdcSpotMarket
					);
					if (lbtcRewards.length > 0) {
						rewardData[LBTC_MARKET_INDEX] = lbtcRewards;
					}
				}
			}

			if (envConfig.cashBonusPerWeek) {
				const cashRewards = calculateCashRewards(
					marketsByIndex[CASH_MARKET_INDEX],
					envConfig.cashBonusPerWeek
				);
				if (cashRewards.length > 0) {
					rewardData[CASH_MARKET_INDEX] = cashRewards;
				}
			}

			if (envConfig.cashBorrowBonusPerWeek) {
				const cashBorrowRewards = calculateCashBorrowRewards(
					marketsByIndex[CASH_MARKET_INDEX],
					envConfig.cashBorrowBonusPerWeek
				);
				if (cashBorrowRewards.length > 0) {
					rewardData[CASH_MARKET_INDEX] = [
						...(rewardData[CASH_MARKET_INDEX] || []),
						...cashBorrowRewards,
					];
				}
			}

			// Calculate interest data for all markets
			const interestData: Record<number, MarketInterestData> = {};

			borrowLendDataList.forEach((marketData: BorrowMarketData) => {
				const marketIndex = marketData.bankConfig.marketIndex;

				try {
					const nativeBreakdowns = nativeData[marketIndex] || [];
					const rewardBreakdowns = rewardData[marketIndex] || [];
					const aprBreakdowns = [...nativeBreakdowns, ...rewardBreakdowns];

					const interestDataForMarket = calculateMarketInterestData(
						marketData,
						aprBreakdowns
					);

					interestData[marketIndex] = interestDataForMarket;
				} catch (error) {
					// Provide fallback data on error
					const fallbackPrecision = marketData.depositApr?.precision ?? 6;
					interestData[marketIndex] = {
						deposit: {
							baseApy: 0,
							totalApy: 0,
							nativeApy: 0,
							promoApy: 0,
							breakdown: [],
							totalApyBN: BigNum.fromPrint('0', fallbackPrecision),
							baseApyBN: BigNum.fromPrint('0', fallbackPrecision),
						},
						borrow: {
							baseApy: 0,
							totalApy: 0,
							rewardApy: 0,
							breakdown: [],
							totalApyBN: BigNum.fromPrint('0', fallbackPrecision),
							baseApyBN: BigNum.fromPrint('0', fallbackPrecision),
						},
					};
				}
			});

			return interestData;
		},
		enabled: !!connection && borrowLendDataList.length > 0,
		staleTime: 30 * 1000,
		gcTime: 5 * 60 * 1000,
		refetchInterval: 5000,
	});

	return {
		data,
		isLoading,
	};
}
